import { Link } from 'react-router-dom'
import { SOCIAL_LINKS } from '@/data/socialLinks'

const FOOTER_LINKS = [
  { label: 'Pricing', href: '/pricing' },
  { label: 'Blog', href: '/blog' },
  { label: 'Changelog', href: '/changelog' },
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
]

function SocialIcon({ platform, label, href }) {
  const icons = {
    x: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    instagram: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
    tiktok: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
    youtube: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-11 h-11 rounded-full bg-[var(--gold)]/10 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--gold)] hover:bg-[var(--gold)]/20 hover:scale-110 transition-all duration-200"
      aria-label={label}
    >
      {icons[platform]}
    </a>
  )
}

export default function Footer() {
  return (
    <footer className="bg-[var(--bg-surface)] relative">
      {/* Gold accent line at top */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[var(--gold)]/40 to-transparent" />

      {/* Edge-to-edge container matches the navbar — responsive padding
          scales from px-4 (mobile) up to px-24 (2xl) so the footer logo
          sits at the same horizontal position as the navbar logo, and
          the social icons hug the right edge on wider monitors. */}
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-16 2xl:px-24 py-12 md:py-16">
        {/* Main row: logo — links — socials */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          {/* Logo — matched to the header (h-14 image, text-2xl wordmark,
              tracking-wide, gap-2.5). Keeps top/bottom chrome visually
              even on every breakpoint. */}
          <Link
            to="/"
            className="flex items-center gap-2.5 text-[var(--gold)] font-heading text-2xl font-bold tracking-wide group shrink-0 hover:scale-105 transition-transform duration-200 origin-left"
          >
            <img
              src="/FinalLogo.png"
              alt="Illuminate Faith logo"
              className="h-14 w-auto rounded"
              style={{ clipPath: 'inset(2% round 4px)' }}
            />
            <span className="leading-none">Illuminate Faith</span>
          </Link>

          {/* Nav links — 2-col grid on mobile (6 links → clean 3 rows of 2),
              flex-wrap on sm+ to fit the row beside the logo/socials.
              text-lg + text-primary matches header NavLink. */}
          <div className="grid w-full max-w-xs grid-cols-2 gap-x-6 gap-y-3 text-center sm:flex sm:w-auto sm:max-w-none sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-9 sm:gap-y-2">
            {FOOTER_LINKS.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className="text-base sm:text-lg font-medium tracking-wide text-[var(--text-primary)] hover:text-[var(--gold)] hover:scale-110 transition-all duration-200 inline-block"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Social icons — bumped to match the bigger header chrome. */}
          <div className="flex items-center gap-4">
            {SOCIAL_LINKS.map(link => (
              <SocialIcon key={link.platform} {...link} />
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mx-auto mt-10 max-w-2xl border-t border-[var(--outline-ghost)] pt-8">
          <p className="text-center text-xs leading-relaxed text-[var(--text-muted-soft)]">
            <span className="font-semibold text-[var(--text-muted-soft)]">
              A note on using Illuminate Faith:
            </span>{' '}
            This tool is a research companion, not a spiritual authority. Everything it produces
            should be tested against Scripture and brought to your church and trusted mentors. Let
            God&apos;s Word — not AI — be your guide.
          </p>
        </div>

        {/* Copyright */}
        <p className="mt-6 text-center text-xs text-[var(--text-muted-soft)]">
          &copy; {new Date().getFullYear()} Illuminate Faith. All rights reserved.
        </p>
      </div>
    </footer>
  )
}
